/**
 * Public models / blueprints browser.
 *
 * Layout: HuggingFace-style compact list. One model per row,
 * full-width horizontal cards, the whole card is clickable.
 *
 * Top bar:
 *   [breadcrumb / count]                 [Search] [Type] [Sort] [Filter]
 *
 * The advanced filters (framework, training environment, sort order)
 * live behind the "Filter" button as a popover - they're rarely used
 * and cluttered the page in the old sidebar layout.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, CircularProgress, Avatar, Tooltip,
  TextField, MenuItem, Select, FormControl, InputLabel, InputAdornment,
  Pagination, Stack, Button, Badge, IconButton, Popover, Divider,
  CardActionArea, useTheme, useMediaQuery,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ModelsIcon } from '../global/CustomIcons';

import { fetchRequest as fetchPublicBlueprints } from '../../redux/actions/blueprints/publicList';
import PageBreadcrumbs from '../common/PageBreadcrumbs';
import { format, formatDistanceToNow } from '../../utils/datetime';

// -----------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------

const KIND_OPTIONS = [
  { value: 'all',                 label: 'All types' },
  { value: 'foundation_backbone', label: 'Foundation model' },
  { value: 'foundation',          label: 'Fine-tuned adapter' },
  { value: 'custom',              label: 'Custom' },
  { value: 'byom',                label: 'BYOM' },
];

// Friendly cap-cased task names for the task chip on foundation rows.
// Single source of truth so nobody hand-cases "forecast" -> "Forecast"
// inconsistently across the codebase.
const TASK_LABELS = {
  forecast:     'Forecast',
  embed:        'Embed',
  impute:       'Impute',
  classify:     'Classify',
  anomaly:      'Anomaly',
  multivariate: 'Multivariate',
};
const formatTask = (t) => TASK_LABELS[t] || (t || '').replace(/^./, (c) => c.toUpperCase());

const SORT_OPTIONS = [
  { value: 'created_at:desc', label: 'Newest' },
  { value: 'created_at:asc',  label: 'Oldest' },
  { value: 'in_use_by:desc',  label: 'Trending' },
  { value: 'rating:desc',     label: 'Top rated' },
  { value: 'name:asc',        label: 'Name A→Z' },
  { value: 'name:desc',       label: 'Name Z→A' },
];

const FRAMEWORK_OPTIONS = ['', 'TensorFlow', 'PyTorch', 'Keras', 'Scikit-learn'];
const ENV_OPTIONS = ['', 'local', 'cloud', 'hybrid'];

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const DEFAULT_FILTERS = {
  page: 1,
  limit: 25,
  search: '',
  kind: 'all',
  framework: '',
  training_environment: '',
  sort_by: 'created_at',
  sort_order: 'desc',
};


// -----------------------------------------------------------------------
// Visual primitives
// -----------------------------------------------------------------------

const RowCard = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  transition: 'border-color 120ms, box-shadow 120ms, transform 120ms',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.6),
    boxShadow: theme.shadows[2],
  },
}));

const TypeChip = ({ label, color = 'default', icon, variant = 'outlined' }) => (
  <Chip
    size="small"
    label={label}
    icon={icon}
    color={color}
    variant={variant}
    sx={{ height: 22, fontSize: 11, fontWeight: 500 }}
  />
);

const kindLabel = (kind) => {
  const found = KIND_OPTIONS.find((k) => k.value === kind);
  return found ? found.label : kind;
};


// -----------------------------------------------------------------------
// One row card
// -----------------------------------------------------------------------

const ModelRow = ({ blueprint, onClick }) => {
  const theme = useTheme();
  const isFoundation = blueprint.kind === 'foundation_backbone';
  const foundation = blueprint.foundation || {};

  // Single-line description, falls back to a placeholder so the row
  // height stays consistent.
  const description = (blueprint.description || '').trim() ||
    (isFoundation
      ? `${foundation.vendor || 'Foundation model'}`
      : 'No description provided.');

  const ownerName = blueprint.owner_details?.name
                 || blueprint.owner_details?.username
                 || 'Anonymous';
  const ownerInitial = ownerName.charAt(0).toUpperCase();

  // Right-side metadata. Trim aggressively; the detail page surfaces
  // license / context / horizon / probabilistic etc. The card just
  // answers "what is this model and how big?" - the two questions
  // that drive the click decision.
  const chips = [];
  if (isFoundation) {
    // Task is the most decision-relevant signal: forecast vs embed
    // vs classify maps directly to "is this useful for me?".
    const tasks = foundation.supports?.tasks || [];
    if (tasks.length > 0) {
      const primary = formatTask(tasks[0]);
      const more = tasks.length > 1 ? ` +${tasks.length - 1}` : '';
      chips.push(
        <TypeChip key="task" label={primary + more} color="primary" />
      );
    }
    // Param count - "120M" / "1.5B" is the universal foundation-model
    // shorthand for capacity / cost.
    const p = foundation.params || foundation.parameter_count;
    if (p) {
      chips.push(
        <TypeChip key="params"
          label={p >= 1e9 ? `${(p / 1e9).toFixed(1)}B`
                          : `${(p / 1e6).toFixed(0)}M`}
        />
      );
    }
    // Variate-ness: tells users at a glance whether the model handles
    // a single channel or many. Surface only when the field is
    // present (undefined => unknown, don't lie about it).
    if (typeof foundation.supports?.multivariate === 'boolean') {
      chips.push(
        <Tooltip
          key="variate"
          arrow
          title={foundation.supports.multivariate
            ? 'Handles multiple correlated series at once'
            : 'Operates on a single time series at a time'}
        >
          <span>
            <TypeChip
              label={foundation.supports.multivariate ? 'Multivariate' : 'Univariate'}
            />
          </span>
        </Tooltip>
      );
    }
    // Family chip: the most useful discriminator now that every
    // backbone shares the same kind.
    if (foundation.family) {
      chips.push(<TypeChip key="family" label={foundation.family} />);
    }
    chips.push(
      <TypeChip key="kind" label={kindLabel(blueprint.kind)} />
    );
  } else {
    chips.push(<TypeChip key="kind" label={kindLabel(blueprint.kind)} />);
    const fw = blueprint.metadata?.framework;
    if (fw) chips.push(<TypeChip key="fw" label={fw} />);
  }

  // Bare date / relative-time label. Prefers created_at (when the
  // model first appeared). No "Added" prefix - the chip row context
  // makes it clear what the date refers to.
  const ts = blueprint.created_at || blueprint.updated_at;
  const tsLabel = ts ? (() => {
    const d = new Date(ts);
    const ageDays = (Date.now() - d.getTime()) / 86400000;
    return ageDays < 30
      ? formatDistanceToNow(d, { addSuffix: true })
      : format(d, 'MMM d, yyyy');
  })() : null;

  return (
    <RowCard variant="outlined">
      <CardActionArea onClick={onClick} sx={{ p: 2 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          {/* Owner avatar - 36px, no halo */}
          <Avatar
            src={blueprint.owner_details?.avatar}
            sx={{
              width: 36, height: 36, fontSize: 13, fontWeight: 600,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              mt: 0.25,
            }}
          >
            {ownerInitial}
          </Avatar>

          {/* Title + meta column - takes all available width so the
              description gets the full row width (no timestamp on the
              right competing with it). */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" fontWeight={600} noWrap
                          sx={{ color: 'text.primary' }}>
                {blueprint.name}
              </Typography>
              <Typography variant="caption" color="text.secondary"
                          sx={{ flexShrink: 0 }}>
                · {ownerName}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" noWrap
                        sx={{ mt: 0.25 }}>
              {description}
            </Typography>
            {/* Chip row: chips on the left, timestamp pinned far-right.
                Wraps on narrow widths via flexWrap so a long chip set
                or small viewport doesn't push the timestamp off-screen.
            */}
            {(chips.length > 0 || tsLabel) && (
              <Stack direction="row" alignItems="center"
                     flexWrap="wrap" useFlexGap rowGap={0.75}
                     sx={{ mt: 1, columnGap: 0.75 }}>
                {chips}
                <Box sx={{ flex: 1 }} />
                {tsLabel && (
                  <Typography variant="caption" color="text.disabled"
                              sx={{ whiteSpace: 'nowrap', ml: 1 }}>
                    {tsLabel}
                  </Typography>
                )}
              </Stack>
            )}
          </Box>

          {/* Chevron stays on the far right, vertically centred. */}
          <ChevronRightIcon
            sx={{ color: 'text.disabled', alignSelf: 'center', flexShrink: 0 }}
          />
        </Stack>
      </CardActionArea>
    </RowCard>
  );
};


// -----------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------

const PublicBlueprintsPage = ({
  fetchPublicBlueprints,
  blueprints, pagination, isLoading, error,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filterAnchor, setFilterAnchor] = useState(null);

  useEffect(() => {
    fetchPublicBlueprints(filters);
  }, [fetchPublicBlueprints, filters]);

  // Bubble featured / recommended models to the top of the current
  // page while preserving the server's sort within each group.
  // ``sort`` is stable in modern JS so equal-rank items keep API order.
  const sortedBlueprints = useMemo(() => {
    if (!Array.isArray(blueprints) || blueprints.length === 0) return blueprints;
    const isFeatured = (bp) =>
      bp?.foundation?.recommended === true || (bp?.featured ?? 0) > 0;
    return [...blueprints].sort((a, b) => {
      const af = isFeatured(a) ? 1 : 0;
      const bf = isFeatured(b) ? 1 : 0;
      return bf - af; // featured first
    });
  }, [blueprints]);

  const sortValue = `${filters.sort_by}:${filters.sort_order}`;
  const activeAdvancedFilters = useMemo(() => {
    let n = 0;
    if (filters.framework) n += 1;
    if (filters.training_environment) n += 1;
    return n;
  }, [filters]);

  const updateFilter = (patch) =>
    setFilters((prev) => ({ ...prev, ...patch, page: 1 }));

  const handleSortChange = (e) => {
    const [sort_by, sort_order] = e.target.value.split(':');
    setFilters((prev) => ({ ...prev, sort_by, sort_order, page: 1 }));
  };

  const handleClearAdvanced = () => {
    setFilters((prev) => ({
      ...prev, framework: '', training_environment: '', page: 1,
    }));
    setFilterAnchor(null);
  };

  const handleViewBlueprint = (blueprint) => {
    navigate(`/model/${blueprint.uid || blueprint._id}`);
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3, bgcolor: alpha(theme.palette.error.main, 0.06) }}>
          <Typography variant="h6" color="error">Error</Typography>
          <Typography color="error">{String(error)}</Typography>
        </Paper>
      </Box>
    );
  }

  const totalLabel = pagination
    ? `${(pagination.total ?? 0).toLocaleString()} models`
    : '...';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* ----- TOP BAR: title (left) + controls (right) ------------ */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <PageBreadcrumbs
            items={[
              { text: 'Models',        link: '/models', icon: <ModelsIcon /> },
              { text: 'Public Models' },
            ]}
          />
          <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
            {totalLabel}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.25} alignItems="center"
               sx={{ width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search models..."
            size="small"
            value={filters.search}
            onChange={(e) => updateFilter({ search: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: { xs: '100%', md: 280 },
              '& .MuiOutlinedInput-root': { borderRadius: 999 },
            }}
          />
          <FormControl size="small">
            <Select
              value={filters.kind}
              onChange={(e) => updateFilter({ kind: e.target.value })}
              sx={{ borderRadius: 999, minWidth: 140 }}
            >
              {KIND_OPTIONS.map((k) => (
                <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <Select
              value={sortValue}
              onChange={handleSortChange}
              sx={{ borderRadius: 999, minWidth: 130 }}
            >
              {SORT_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Advanced filters">
            <IconButton
              onClick={(e) => setFilterAnchor(e.currentTarget)}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: activeAdvancedFilters > 0
                  ? alpha(theme.palette.primary.main, 0.08)
                  : 'transparent',
              }}
            >
              <Badge badgeContent={activeAdvancedFilters} color="primary">
                <FilterAltIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Advanced filters popover */}
      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1, p: 2, width: 280, borderRadius: 2 } }}
      >
        <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
            Advanced filters
          </Typography>
          {activeAdvancedFilters > 0 && (
            <Button size="small" startIcon={<FilterAltOffIcon fontSize="small" />}
                    onClick={handleClearAdvanced}>
              Clear
            </Button>
          )}
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <FormControl size="small" fullWidth sx={{ mb: 2 }}>
          <InputLabel>Framework</InputLabel>
          <Select
            label="Framework"
            value={filters.framework}
            onChange={(e) => updateFilter({ framework: e.target.value })}
          >
            {FRAMEWORK_OPTIONS.map((f) => (
              <MenuItem key={f || 'any'} value={f}>
                {f || 'Any framework'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>Training environment</InputLabel>
          <Select
            label="Training environment"
            value={filters.training_environment}
            onChange={(e) => updateFilter({ training_environment: e.target.value })}
          >
            {ENV_OPTIONS.map((env) => (
              <MenuItem key={env || 'any'} value={env}>
                {env ? env.charAt(0).toUpperCase() + env.slice(1) : 'Any'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Popover>

      {/* ----- LIST OF ROWS --------------------------------------- */}
      {isLoading && !blueprints?.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : sortedBlueprints?.length > 0 ? (
        <Stack spacing={1}>
          {sortedBlueprints.map((bp) => (
            <ModelRow
              key={bp.uid || bp._id}
              blueprint={bp}
              onClick={() => handleViewBlueprint(bp)}
            />
          ))}
        </Stack>
      ) : (
        <Paper sx={{
          p: 5, textAlign: 'center', border: 1, borderColor: 'divider',
          borderStyle: 'dashed', bgcolor: 'transparent',
        }}>
          <ModelsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            No models found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {filters.search
              ? `No results match "${filters.search}"`
              : 'Try adjusting your filters.'}
          </Typography>
          {(filters.search || filters.kind !== 'all' || activeAdvancedFilters > 0) && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterAltOffIcon />}
              onClick={() => setFilters(DEFAULT_FILTERS)}
              sx={{ mt: 2 }}
            >
              Clear filters
            </Button>
          )}
        </Paper>
      )}

      {/* ----- PAGINATION ---------------------------------------- */}
      {pagination && pagination.total > 0 && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ mt: 3 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Showing
            </Typography>
            <FormControl size="small">
              <Select
                value={filters.limit}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    limit: parseInt(e.target.value, 10),
                    page: 1,
                  }))
                }
                sx={{
                  borderRadius: 999,
                  '& .MuiSelect-select': { py: 0.5, pr: '24px !important' },
                }}
              >
                {ROWS_PER_PAGE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              of {pagination.total.toLocaleString()}
            </Typography>
          </Stack>
          <Pagination
            count={pagination.page_count}
            page={pagination.page}
            onChange={(_, p) => setFilters((prev) => ({ ...prev, page: p }))}
            shape="rounded"
            size={isMobile ? 'small' : 'medium'}
          />
        </Stack>
      )}
    </Box>
  );
};

const mapStateToProps = (state) => ({
  blueprints: state.blueprints_public_list.response.models,
  pagination: state.blueprints_public_list.response.pagination,
  isLoading:  state.blueprints_public_list.isLoading,
  error:      state.blueprints_public_list.error,
});

const mapDispatchToProps = (dispatch) => ({
  fetchPublicBlueprints: (filters) => dispatch(fetchPublicBlueprints(filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PublicBlueprintsPage);
